/**
 * Chemical and mathematical formulas inside the HTML of a text element.
 *
 * A formula is saved as a span that keeps what the teacher typed (mhchem notation for chemistry,
 * LaTeX for math) plus a plain Unicode version of it, such as H₂O or SO₄²⁻:
 *
 *   <span class="ils-formula" data-kind="chem" data-source="H2O" data-display="inline" translate="no">H₂O</span>
 *
 * The editor and the player draw the formula from `data-source` with KaTeX. Anything that does not,
 * such as search, translation and the AI assistant, still reads a sensible formula from the text.
 */

export type FormulaKind = 'chem' | 'math'

export interface Formula {
  kind: FormulaKind
  /** What the teacher typed: mhchem notation for chemistry, LaTeX for math. */
  source: string
  /** True when the formula sits on its own line instead of inside the sentence. */
  display: boolean
}

export const FORMULA_CLASS = 'ils-formula'

/** The TeX that KaTeX renders for a formula. */
export const formulaToTex = (formula: Pick<Formula, 'kind' | 'source'>): string =>
  formula.kind === 'chem' ? `\\ce{${formula.source}}` : formula.source

export const containsFormula = (html: string | undefined | null): boolean => !!html && html.includes(FORMULA_CLASS)

const escapeHtml = (text: string): string =>
  text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')

/** The HTML saved for one formula. */
export function formulaToHtml(formula: Formula): string {
  return (
    `<span class="${FORMULA_CLASS}" data-kind="${formula.kind}" data-source="${escapeHtml(formula.source)}"` +
    ` data-display="${formula.display ? 'block' : 'inline'}" translate="no">${escapeHtml(formulaFallbackText(formula))}</span>`
  )
}

/** Reads a formula from the attributes of a saved formula span, whatever kind of node that is. */
export function readFormulaAttributes(getAttribute: (name: string) => string | null | undefined): Formula {
  return {
    kind: getAttribute('data-kind') === 'math' ? 'math' : 'chem',
    source: getAttribute('data-source') ?? '',
    display: getAttribute('data-display') === 'block',
  }
}

export const formulaFallbackText = (formula: Pick<Formula, 'kind' | 'source'>): string =>
  formula.kind === 'chem' ? chemToUnicode(formula.source) : mathToText(formula.source)

const SUBSCRIPTS: Record<string, string> = {
  '0': '₀', '1': '₁', '2': '₂', '3': '₃', '4': '₄', '5': '₅', '6': '₆', '7': '₇', '8': '₈', '9': '₉',
  '+': '₊', '-': '₋', '(': '₍', ')': '₎',
}
const SUPERSCRIPTS: Record<string, string> = {
  '0': '⁰', '1': '¹', '2': '²', '3': '³', '4': '⁴', '5': '⁵', '6': '⁶', '7': '⁷', '8': '⁸', '9': '⁹',
  '+': '⁺', '-': '⁻', '(': '⁽', ')': '⁾', I: 'ᴵ', V: 'ⱽ',
}

/** Unicode has sub- and superscripts for digits and signs only; anything else keeps a ^ or _ marker. */
function toScript(text: string, table: Record<string, string>, marker: string): string {
  const characters = [...text]
  if (characters.every((c) => c in table)) {
    return characters.map((c) => table[c]).join('')
  }
  return text.length === 1 ? `${marker}${text}` : `${marker}(${text})`
}

const superscript = (text: string) => toScript(text, SUPERSCRIPTS, '^')
const subscript = (text: string) => toScript(text, SUBSCRIPTS, '_')

const ARROWS: Record<string, string> = { '<=>': '⇌', '<->': '↔', '->': '→', '<-': '←' }

/** Plain Unicode version of mhchem notation, for example `SO4^2-` becomes SO₄²⁻. */
export function chemToUnicode(source: string): string {
  let text = ` ${source.trim()} `
  text = text.replace(/\\Delta/g, 'Δ').replace(/\\([a-zA-Z]+)/g, '$1')
  // Arrows with conditions above or below them, such as ->[\Delta]
  text = text.replace(
    /(<=>|<->|->|<-)\[([^\]]*)\](?:\[([^\]]*)\])?/g,
    (_match, arrow: string, above: string, below?: string) =>
      `${ARROWS[arrow]}(${[above, below].filter(Boolean).join(', ')})`,
  )
  text = text.replace(/<=>>|<<=>|<=>/g, '⇌').replace(/<->/g, '↔').replace(/->/g, '→').replace(/<-/g, '←')
  // Isotopes: ^{14}_{6}C
  text = text.replace(/\^\{?(\d+)\}?_\{?(-?\d+)\}?/g, (_match, mass: string, number: string) => superscript(mass) + subscript(number))
  // Explicit charges and oxidation numbers: ^{2+}, ^2-, ^+, ^{III}
  text = text.replace(/\^\{([^}]*)\}/g, (_match, script: string) => superscript(script))
  text = text.replace(/\^(\d*[+-])/g, (_match, charge: string) => superscript(charge))
  // Gas escapes ( ^ ) and precipitate ( v ), written as separate words
  text = text.replace(/ \^(?= )/g, ' ↑').replace(/ v(?= )/g, ' ↓')
  // Water of crystallisation: CuSO4*5H2O
  text = text.replace(/\s*\*\s*/g, '·')
  // A number followed by a sign at the end of a particle is a charge: Cu2+
  text = text.replace(/([A-Za-z)\]])(\d+)([+-])(?=[\s(]|$)/g, (_match, before: string, digits: string, sign: string) => before + superscript(digits + sign))
  // Other numbers after an element or bracket are subscripts: H2O
  text = text.replace(/([A-Za-z)\]])(\d+)/g, (_match, before: string, digits: string) => before + subscript(digits))
  // A sign directly after a particle is a charge: Na+, Cl-, e-
  text = text.replace(/([A-Za-z₀-₉)\]])([+-])(?=[\s(]|$)/g, (_match, before: string, sign: string) => before + superscript(sign))
  return text.replace(/\s+/g, ' ').trim()
}

/** Returns the contents of the brace group starting at `start`, and the index after it. */
function readBraceGroup(text: string, start: number): [string, number] {
  let depth = 0
  for (let i = start; i < text.length; i++) {
    if (text[i] === '{') {
      depth++
    } else if (text[i] === '}' && --depth === 0) {
      return [text.slice(start + 1, i), i + 1]
    }
  }
  return [text.slice(start + 1), text.length]
}

/** Replaces `\name{a}{b}` by replace(a, b), respecting nested braces. */
function replaceCommand(text: string, name: string, argumentCount: number, replace: (...args: string[]) => string): string {
  const command = `\\${name}`
  let result = ''
  let index = 0
  while (index < text.length) {
    const found = text.indexOf(command, index)
    if (found < 0) {
      result += text.slice(index)
      break
    }
    const afterCommand = found + command.length
    if (/[a-zA-Z]/.test(text[afterCommand] ?? '')) {
      // A longer command that starts with the same letters, such as \centerdot for \ce
      result += text.slice(index, afterCommand)
      index = afterCommand
      continue
    }
    result += text.slice(index, found)
    let next = afterCommand
    const args: string[] = []
    for (let a = 0; a < argumentCount; a++) {
      while (text[next] === ' ') next++
      if (text[next] === '{') {
        const [group, end] = readBraceGroup(text, next)
        args.push(group)
        next = end
      } else {
        args.push(text[next] ?? '')
        next++
      }
    }
    result += replace(...args)
    index = next
  }
  return result
}

const MATH_SYMBOLS: Record<string, string> = {
  Delta: 'Δ', delta: 'δ', alpha: 'α', beta: 'β', gamma: 'γ', lambda: 'λ', mu: 'μ', nu: 'ν', pi: 'π',
  cdot: '·', times: '×', approx: '≈', circ: '°', to: '→', rightarrow: '→', leq: '≤', geq: '≥', pm: '±',
}

const bracketWhenCompound = (text: string): string => (/[\s+\-−]/.test(text.trim()) ? `(${text.trim()})` : text.trim())

/** Rough plain-text version of LaTeX, for example `\frac{m}{M}` becomes m/M. */
export function mathToText(source: string): string {
  let text = source
  text = replaceCommand(text, 'ce', 1, (formula) => chemToUnicode(formula))
  text = replaceCommand(text, 'pu', 1, (quantity) =>
    quantity
      .trim()
      .replace(/^-/, '−')
      .replace(/([A-Za-z])(-?\d+)(?![\d.])/g, (_match, unit: string, power: string) => unit + superscript(power)),
  )
  text = replaceCommand(
    text,
    'frac',
    2,
    (numerator, denominator) => `${bracketWhenCompound(mathToText(numerator))}/${bracketWhenCompound(mathToText(denominator))}`,
  )
  text = replaceCommand(text, 'sqrt', 1, (radicand) => `√(${mathToText(radicand)})`)
  text = replaceCommand(text, 'mathrm', 1, (content) => content)
  text = replaceCommand(text, 'text', 1, (content) => content)
  text = text.replace(/\^\{\\circ\}/g, '°').replace(/\\([a-zA-Z]+)/g, (_match, name: string) => MATH_SYMBOLS[name] ?? name)
  text = text.replace(/\^\{([^{}]*)\}/g, (_match, script: string) => superscript(script))
  text = text.replace(/\^(\w)/g, (_match, script: string) => superscript(script))
  text = text.replace(/_\{([^{}]*)\}/g, (_match, script: string) => subscript(script))
  text = text.replace(/_(\w)/g, (_match, script: string) => subscript(script))
  text = text.replace(/\\[,;! ]/g, ' ').replace(/[{}]/g, '').replace(/(^|[\s=(])-/g, '$1−')
  return text.replace(/\s+/g, ' ').trim()
}
